import { Alert } from '@creditornot/cb-alert';
import React, { useMemo, useContext } from 'react';
import styled from 'styled-components';

import { ItemConfiguration } from 'apis/picker-api/types';
import { Item, ItemCommission, Menu } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { localize } from 'common/utils';
import { MenuLocaleContext } from 'modules/menu/MenuLocaleContext';
import LazyLoadedThumbnail from 'modules/menu/components/LazyLoadedThumbnail';
import MenuEditorSidepanel from 'modules/menu/components/MenuEditorSidepanel';
import useMenuData from 'modules/menu/useMenuData';

import EmptyThumbnail from './EmptyThumbnail';
import ItemDetails from './Item/ItemDetails';
import PurchasabilityErrorList from './Item/PurchasabilityErrorList';
import { getPurchasabilityValidationErrors } from './Item/utils';
import { ItemQuantity } from '../types';

type Props = {
  item: Item;
  menu: Menu;
  itemQuantity: ItemQuantity | undefined;
  onItemBalanceUpdated: () => void;
  onClose: () => void;
  itemConfig: ItemConfiguration | undefined;
  itemCommissions: ItemCommission[] | undefined;
  onItemConfigUpdated: (updatedConfigs: Record<string, ItemConfiguration>) => void;
  onItemCommissionsUpdated: () => void;
};

const Content = styled.div`
  padding: 0 24px;
`;

const StyledAlert = styled(Alert)`
  margin-block-start: 24px;
`;

const StyledThumbnail = styled(LazyLoadedThumbnail)`
  width: 48px;
  height: 48px;
`;

const StyledPlaceholder = styled(EmptyThumbnail)`
  width: 48px;
  height: 48px;
`;

const ItemSidePanel = ({
  item,
  itemQuantity,
  menu,
  onItemBalanceUpdated,
  onClose,
  itemConfig,
  itemCommissions,
  onItemConfigUpdated,
  onItemCommissionsUpdated,
}: Props) => {
  const { primaryLanguage } = useContext(MenuLocaleContext);
  const itemName = localize(item.name, primaryLanguage);

  const { options } = useMenuData();

  const purchasabilityValidationErrors = useMemo(
    () => getPurchasabilityValidationErrors(options, item),
    [options, item],
  );

  return (
    <MenuEditorSidepanel
      id={item.id}
      thumbnail={
        item.image ? (
          <StyledThumbnail alt={itemName} blurhash={item.image_blurhash} src={item.image} />
        ) : (
          <StyledPlaceholder />
        )
      }
      title={itemName}
      onClose={onClose}
    >
      {({ editing, setEditing }) => (
        <Content>
          {purchasabilityValidationErrors.length > 0 && (
            <StyledAlert variant="error">
              <PurchasabilityErrorList
                primaryLanguage={menu.primary_language}
                purchasabilityValidationErrors={purchasabilityValidationErrors}
              />
            </StyledAlert>
          )}
          <ItemDetails
            item={item}
            menu={menu}
            itemQuantity={itemQuantity}
            editing={editing}
            onSaveSuccess={() => setEditing(false)}
            onItemBalanceUpdated={onItemBalanceUpdated}
            onCancel={() => setEditing(false)}
            options={options}
            itemConfig={itemConfig}
            itemCommissions={itemCommissions}
            onItemConfigUpdated={onItemConfigUpdated}
            onItemCommissionsUpdated={onItemCommissionsUpdated}
          />
        </Content>
      )}
    </MenuEditorSidepanel>
  );
};

export default ItemSidePanel;
